import { useCallback, useEffect, useState } from "react";
import localStorageConstants from "src/app/constants/localStorage.constants";
import {
  getLogs,
  getParentChildren,
  searchUsers,
} from "src/app/utils/backendEndpointCalls.util";

export interface LogEntry {
  id: string;
  type: string;
  createdAt: string;
  text: string;
}

export interface LogsPagination {
  page: number;
  pageSize: number;
  totalLogs: number;
  totalPages: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
}

interface ParentChild {
  _id: string;
  firstName: string;
}

interface AdminUser {
  id: string;
  firstName: string;
  lastName: string;
}

interface SelectorUser {
  id: string;
  label: string;
}

const defaultPagination: LogsPagination = {
  page: 1,
  pageSize: 20,
  totalLogs: 0,
  totalPages: 0,
  hasPreviousPage: false,
  hasNextPage: false,
};

const LogsScreenViewModel = () => {
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [pagination, setPagination] =
    useState<LogsPagination>(defaultPagination);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [isLoadingLogs, setIsLoadingLogs] = useState<boolean>(false);

  const [isSnackbarError, setIsSnackbarError] = useState<boolean>(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>("");
  const [isDisplayingSnackbar, setIsDisplayingSnackbar] =
    useState<boolean>(false);

  const [role, setRole] = useState<string>("");
  const [selectorUsers, setSelectorUsers] = useState<SelectorUser[]>([]);
  const [isLoadingSelectorUsers, setIsLoadingSelectorUsers] =
    useState<boolean>(false);
  const [selectedLogsOwnerId, setSelectedLogsOwnerId] = useState<string>("all");

  const showError = (message: string) => {
    setIsSnackbarError(true);
    setSnackbarMessage(message);
    setIsDisplayingSnackbar(true);
  };

  useEffect(() => {
    const storedRole = localStorage.getItem(localStorageConstants.ROLE) ?? "";
    setRole(storedRole);
  }, []);

  const getSelectedUserId = useCallback((): string | undefined => {
    if (selectedLogsOwnerId === "all") return undefined;
    if (selectedLogsOwnerId === "you") return "own";
    return selectedLogsOwnerId;
  }, [selectedLogsOwnerId]);

  const loadParentChildren = useCallback(async () => {
    setIsLoadingSelectorUsers(true);
    const response = await getParentChildren();
    setIsLoadingSelectorUsers(false);

    if (!response.success) {
      showError(response.error);
      return;
    }

    const fetchedChildren: ParentChild[] = response.children ?? [];
    setSelectorUsers([
      { id: "you", label: "You" },
      ...fetchedChildren.map((child: ParentChild) => ({
        id: child._id,
        label: child.firstName,
      })),
    ]);
  }, []);

  const loadAdminUsers = useCallback(async () => {
    setIsLoadingSelectorUsers(true);
    const response = await searchUsers("");
    setIsLoadingSelectorUsers(false);

    if (!response.success) {
      showError(response.error);
      return;
    }

    const fetchedUsers: AdminUser[] = response.users ?? [];
    setSelectorUsers([
      { id: "you", label: "You" },
      ...fetchedUsers.map((user: AdminUser) => ({
        id: user.id,
        label: `${user.firstName} ${user.lastName}`,
      })),
    ]);
  }, []);

  const loadLogs = useCallback(async (page: number, userId?: string) => {
    setIsLoadingLogs(true);
    const response = await getLogs(page, "warpa", userId);
    setIsLoadingLogs(false);

    if (!response.success) {
      showError(response.error);
      return;
    }

    setLogs(response.logs ?? []);
    setPagination(response.pagination ?? defaultPagination);
  }, []);

  useEffect(() => {
    if (role === "Parent") {
      loadParentChildren();
      return;
    }

    if (role === "Admin") {
      loadAdminUsers();
      return;
    }

    setSelectorUsers([]);
  }, [loadAdminUsers, loadParentChildren, role]);

  useEffect(() => {
    loadLogs(currentPage, getSelectedUserId());
  }, [currentPage, getSelectedUserId, loadLogs, selectedLogsOwnerId]);

  const onPreviousPagePressed = () => {
    if (!pagination.hasPreviousPage) return;
    setCurrentPage((prev) => prev - 1);
  };

  const onNextPagePressed = () => {
    if (!pagination.hasNextPage) return;
    setCurrentPage((prev) => prev + 1);
  };

  const onRefreshPressed = () => {
    loadLogs(currentPage, getSelectedUserId());
  };

  const onSelectedLogsOwnerChanged = (ownerId: string) => {
    setSelectedLogsOwnerId(ownerId);
    setCurrentPage(1);
  };

  const formatDateTime = (value: string): string => {
    const parsedDate = new Date(value);
    if (Number.isNaN(parsedDate.getTime())) return value;

    return parsedDate.toLocaleString();
  };

  const getLogTypeChipColor = (
    type: string,
  ): "default" | "primary" | "secondary" | "success" | "warning" | "error" => {
    const normalizedType = type.trim().toLowerCase();

    if (
      normalizedType.includes("error") ||
      normalizedType.includes("fail") ||
      normalizedType.includes("reject")
    )
      return "error";

    if (
      normalizedType.includes("warning") ||
      normalizedType.includes("suspend")
    )
      return "warning";

    if (
      normalizedType.includes("success") ||
      normalizedType.includes("accept") ||
      normalizedType.includes("created")
    )
      return "success";

    if (normalizedType.includes("update") || normalizedType.includes("edit"))
      return "primary";

    if (normalizedType.includes("link") || normalizedType.includes("relation"))
      return "secondary";

    return "default";
  };

  return {
    logs,
    pagination,
    currentPage,
    isLoadingLogs,
    onPreviousPagePressed,
    onNextPagePressed,
    onRefreshPressed,
    formatDateTime,
    getLogTypeChipColor,
    isSnackbarError,
    snackbarMessage,
    isDisplayingSnackbar,
    setIsDisplayingSnackbar,
    role,
    selectorUsers,
    isLoadingSelectorUsers,
    selectedLogsOwnerId,
    onSelectedLogsOwnerChanged,
  };
};

export default LogsScreenViewModel;
